import { TEXT } from "../talentMobility.constants";

export const PRIORITY_TAG_COLOR = { High: "warning", Normal: "default", Low: "default" };

export const PLANNING_TOOLS_MENU_ITEMS = [
  { key: "redeployment", label: TEXT.BTN_REDEPLOYMENT_CENTER },
  { key: "workforce", label: TEXT.BTN_WORKFORCE_PLANNING },
  { key: "paicOrgRedesign", label: TEXT.BTN_SIMULATE_PAIC },
  { key: "viewPlanning", label: TEXT.BTN_VIEW_PLANNING },
];

export const PLANNING_TOOLS_ROUTES = {
  redeployment: "/talent-mobility/redeployment-center",
  workforce: "/talent-mobility/workforce-planning",
  viewPlanning: "/talent-mobility/view-planning",
  paicOrgRedesign: "/talent-mobility/paic-org-redesign",
};

// ─── Talent Mobility (HR / Line Manager) guided tour ──────────────────────────
export const TOUR_MODULE_KEY = "talent-mobility";

// Cross-page tour phases (namespaced). The tour opens the first role's Role
// Detail, then its top candidate's profile, comes back here for the Planning
// Tools step, then walks the three Planning Tools pages before returning here
// for Finish. Each phase names the page (or resume point) that runs next.
export const CROSS_PAGE_TOUR_PHASE = {
  ROLE_DETAIL: "talent-mobility:role-detail",
  CANDIDATE: "talent-mobility:candidate",
  PLANNING_TOOLS: "talent-mobility:planning-tools", // back here, resume at Planning Tools
  REDEPLOYMENT: "talent-mobility:redeployment-center",
  WORKFORCE: "talent-mobility:workforce-planning",
  VIEW_PLANS: "talent-mobility:view-plans",
  FINISH: "talent-mobility:finish", // back here, resume at Finish
};

// Column steps target the real header cell by class (AntD duplicates the
// title node); the row steps target the first visible row by class.
export const TOUR_COLUMN_CLASS = {
  priority: "tm-tour-priority-col",
  openDiscoverable: "tm-tour-open-col",
  matches: "tm-tour-matches-col",
  roleTitle: "tm-tour-title-col",
};
export const TOUR_FIRST_ROW_CLASS = "tm-tour-first-row";

// `state`: "always" runs in every branch; "data" only when the roster has
// roles, "empty" only when it doesn't. `page` steps are shown by another page.
export const TOUR_STEPS = [
  {
    // Welcome — centered greeting
    state: "always",
    title: "Welcome to Talent Mobility",
    description:
      "This is where you match the people you already have to the roles you need filled, and plan for the ones you'll need next. Let's take a quick tour.",
    primaryText: "Start the Tour",
    secondaryText: "I'll explore on my own",
  },
  {
    state: "data",
    key: "rolesTable",
    title: "Roles Table",
    description:
      "Every role in your scope, one row each — requisition number, title, business unit, function, sub-function, location and level. Every one of those columns filters, so you can narrow straight down to the part of the org you care about.",
    placement: "top",
  },
  {
    // Shown only when the roster is empty (replaces the table steps).
    state: "empty",
    key: "emptyState",
    title: "No Roles Yet",
    description:
      "No roles to show yet. Talent Mobility reads from your employee roster — once HR onboards employees in Admin Setup › Employee Data, their roles appear here with match scores.",
    placement: "top",
  },
  {
    state: "data",
    key: "priority",
    title: "Priority Column",
    description:
      "Priority is read-only here — it's whatever the role is marked as in the Jobs Library. Change it there, not here.",
    placement: "left",
  },
  {
    state: "data",
    key: "openDiscoverable",
    title: "Open & Discoverable Columns",
    description:
      "Open counts how many of a role's people have their position marked open; Discoverable counts how many are visible to employees browsing career pathways. Both read as 'how many of how many'.",
    placement: "left",
  },
  {
    state: "data",
    key: "matches",
    title: "Matches Column",
    description:
      "Matches counts the people whose Skill Match Index clears your organisation's green threshold for that role. The threshold itself is set in Admin Setup › Talent Mobility Settings.",
    placement: "left",
  },
  {
    state: "data",
    key: "roleRow",
    title: "Expand a Role",
    description:
      "Click a role, or its caret under View current employees, to open it in place and see the people currently in it, each with their own Open and Discoverable checkboxes.",
    placement: "bottom",
  },
  {
    state: "data",
    key: "roleTitle",
    title: "Potential Successors",
    description:
      "Click a role's title to see who could step into it — every internal candidate, ranked by how well their skills match that role.",
    placement: "bottom",
  },
  {
    state: "data",
    key: "roleDetailToggle",
    page: CROSS_PAGE_TOUR_PHASE.ROLE_DETAIL,
    title: "All vs. Applicants",
    description:
      "All shows every internal candidate we scored against this role. Applicants narrows it to the people who actually applied.",
    placement: "bottom",
  },
  {
    state: "data",
    key: "candidateProfile",
    page: CROSS_PAGE_TOUR_PHASE.CANDIDATE,
    title: "Candidate Profile",
    description:
      "Open a candidate to see their match score, their skills against the role's requirements — as a gap list or a bubble map — and their profile summary. From here you can shortlist them, mark them available, or recommend them.",
    placement: "top",
  },
  {
    state: "data",
    key: "planningTools",
    resumePhase: CROSS_PAGE_TOUR_PHASE.PLANNING_TOOLS,
    title: "Planning Tools",
    description:
      "Planning Tools is where the forward-looking work lives: Redeployment Center for roles you're shrinking, Workforce Planning for roles you're growing, and View Plans for everything you've saved.",
    placement: "bottom",
  },
  {
    state: "data",
    key: "redeploymentCenter",
    page: CROSS_PAGE_TOUR_PHASE.REDEPLOYMENT,
    title: "Redeployment Center",
    description:
      "Redeployment Center works in two directions. Role View starts from a role: lower its Target Positions below its Current Positions to plan a reduction and see who's affected. Employee View starts from a person and ranks the other roles they could move into.",
    placement: "bottom",
  },
  {
    state: "data",
    key: "workforcePlanning",
    page: CROSS_PAGE_TOUR_PHASE.WORKFORCE,
    title: "Workforce Planning",
    description:
      "Workforce Planning is a three-step pass: set the demand for a role you're growing, review the internal candidates who could fill it, then read the summary across every role you've planned.",
    placement: "bottom",
  },
  {
    state: "data",
    key: "viewPlans",
    page: CROSS_PAGE_TOUR_PHASE.VIEW_PLANS,
    title: "View Plans",
    description:
      "Every Redeployment and Workforce plan you've saved lives here, each one opening back into a read-only copy of the flow that created it.",
    placement: "bottom",
  },
  {
    // Finish — centered greeting. Primary opens the Planning Tools menu.
    state: "always",
    resumePhase: CROSS_PAGE_TOUR_PHASE.FINISH,
    title: "You're all set!",
    description: "Ready to plan your first move?",
    primaryText: "Open Planning Tools",
    secondaryText: "Done",
  },
];

// Every step for the current branch, in spec order — including the steps
// other pages show, so this page can tell when advancing should hop pages.
export const getTourSteps = (isEmpty) =>
  TOUR_STEPS.filter(
    (step) => step.state === "always" || step.state === (isEmpty ? "empty" : "data"),
  );

// The steps a given page shows on the tour's behalf.
export const getTourLegSteps = (phase) => TOUR_STEPS.filter((step) => step.page === phase);
